import React, { useState, useRef, useEffect } from "react";
import { useAuthStore } from "@/stores/auth.store";
import { trpcClient } from "@/lib/trpc";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ScanBarcode, Send, Factory, Search, CheckCircle2, Loader2, Zap } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { NumericInput } from "@/components/ui/numeric-input";
import { getStatusLabel } from "@/config/status-registry";

export interface ShopfloorScannerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialCode?: string;
  onOpenLogForm?: (prefill: any) => void;
  onSelectPo?: (poId: string, poCode: string) => void;
}

const formatStatusVi = (st?: string) => {
  if (!st) return "Đang làm";
  return getStatusLabel("RoutingStepStatus", st);
};

export function ShopfloorScannerModal({
  open,
  onOpenChange,
  initialCode,
  onOpenLogForm,
  onSelectPo,
}: ShopfloorScannerModalProps) {
  const user = useAuthStore(s => s.user);
  const [scanCode, setScanCode] = useState(initialCode || "");
  const [activeCode, setActiveCode] = useState(initialCode || "");
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (initialCode) {
      setScanCode(initialCode);
      setActiveCode(initialCode);
    }
  }, [initialCode]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  const handleScan = (e: React.FormEvent) => {
    e.preventDefault();
    if (!scanCode.trim()) return;
    setActiveCode(scanCode.trim());
  };

  const { data: scanResult, isLoading, isError, error } = useQuery({
    queryKey: ["shopfloor", "scan", activeCode],
    queryFn: async () => {
      if (!activeCode) return null;
      return await trpcClient.shopfloor.scan.query({ code: activeCode });
    },
    enabled: !!activeCode && open,
  });

  useEffect(() => {
    if (isError) {
      toast.error(error instanceof Error ? error.message : "Lỗi khi quét mã lệnh");
    }
  }, [isError, error]);

  const completeMutation = useMutation({
    mutationFn: async (params: { productionOrderId: string; routingStepId?: string; processId?: string; qtyOk: number; qtyNg: number }) => {
      if (!params.routingStepId) {
        throw new Error("Vui lòng chọn công đoạn sản xuất để ghi nhận sản lượng.");
      }
      return await trpcClient.productionLogs.create.mutate({
        productionOrderId: params.productionOrderId,
        routingStepId: params.routingStepId,
        qtyOk: params.qtyOk,
        qtyNg: params.qtyNg,
        notes: `Ghi nhận nhanh qua Quét mã vạch sàn xưởng`,
      });
    },
    onSuccess: () => {
      toast.success("Đã ghi nhận sản lượng thành công!");
      queryClient.invalidateQueries({ queryKey: ["production-logs"] });
      queryClient.invalidateQueries({ queryKey: ["production-order"] });
      queryClient.invalidateQueries({ queryKey: ["machine-logs"] });
      queryClient.invalidateQueries({ queryKey: ["shopfloor", "scan", activeCode] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi khi ghi nhận số lượng sản xuất");
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-indigo-700">
            <ScanBarcode className="w-6 h-6 text-indigo-600" />
            Quét mã vạch Lệnh Sản Xuất
          </DialogTitle>
          <DialogDescription>
            Quét mã vạch hoặc nhập mã Lệnh Sản Xuất (VD: LSX-2606-001) để xem và ghi nhận sản lượng.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <form onSubmit={handleScan} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                ref={inputRef}
                value={scanCode}
                onChange={(e) => setScanCode(e.target.value)}
                placeholder="Quét mã vạch hoặc nhập mã lệnh sản xuất..."
                className="pl-9 h-11 text-base font-medium"
                autoComplete="off"
              />
            </div>
            <Button type="submit" className="h-11 px-6 bg-indigo-600 hover:bg-indigo-700" disabled={isLoading || !scanCode.trim()}>
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tìm kiếm"}
            </Button>
          </form>

          {isLoading && (
            <div className="space-y-3 py-4">
              <Skeleton className="h-20 w-full rounded-lg" />
              <Skeleton className="h-32 w-full rounded-lg" />
            </div>
          )}

          {scanResult && !isLoading && (
            <div className="space-y-4 pt-2">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{scanResult.productionOrder?.poCode || activeCode}</h3>
                    <p className="text-sm text-slate-600 font-medium">
                      Sản phẩm: {scanResult.productionOrder?.productName || "—"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                      {formatStatusVi(scanResult.productionOrder?.status)}
                    </Badge>
                    {onSelectPo && (
                      <Button
                        type="button"
                        size="sm"
                        className="h-7 px-2.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white"
                        onClick={() => {
                          onSelectPo(scanResult.productionOrder.id, scanResult.productionOrder.poCode || scanResult.productionOrder.orderId);
                          onOpenChange(false);
                        }}
                      >
                        Chọn Lệnh Này
                      </Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-slate-200 text-sm">
                  <div>
                    <span className="text-slate-500">SL Kế hoạch:</span>{" "}
                    <span className="font-bold text-slate-800">{scanResult.productionOrder?.qtyPlan?.toLocaleString() || 0}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Đã sản xuất:</span>{" "}
                    <span className="font-bold text-emerald-600">{scanResult.productionOrder?.qtyProduced?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>

              {scanResult.relevantTasks && scanResult.relevantTasks.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    Công đoạn sản xuất ({scanResult.relevantTasks.length})
                  </h4>
                  {scanResult.relevantTasks.map((task: any) => (
                    <TaskCardItem 
                      key={task.id || task.routingStepId} 
                      task={task} 
                      onComplete={(qtyOk, qtyNg) => {
                        completeMutation.mutate({
                          productionOrderId: scanResult.productionOrder.id,
                          routingStepId: task.routingStepId || task.id,
                          processId: task.stepId || task.processId,
                          qtyOk,
                          qtyNg
                        });
                      }}
                      onOpenLogForm={onOpenLogForm ? () => {
                        onOpenChange(false);
                        onOpenLogForm({
                          productionOrderId: scanResult.productionOrder.id,
                          poCode: scanResult.productionOrder.poCode || scanResult.productionOrder.orderId,
                          productName: scanResult.productionOrder.productName,
                          routingStepId: task.routingStepId || task.id,
                          stepName: task.processName,
                          stepOrder: task.stepOrder,
                          machineId: task.machineId || undefined,
                          machineName: task.machineName,
                          standardQuantity: task.qtyPlan,
                          actualQtyOk: task.qtyOk,
                          remainingQty: Math.max(0, (task.qtyPlan || 0) - (task.qtyOk || 0)),
                        });
                      } : undefined}
                      isLoading={completeMutation.isPending}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-amber-800 font-medium">
                    Lệnh sản xuất này chưa được thiết lập công đoạn quy trình (Routing Steps). Vui lòng kiểm tra lại kế hoạch sản xuất.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TaskCardItem({
  task,
  onComplete,
  onOpenLogForm,
  isLoading,
}: {
  task: any;
  onComplete: (qtyOk: number, qtyNg: number) => void;
  onOpenLogForm?: () => void;
  isLoading: boolean;
}) {
  const [qtyOk, setQtyOk] = useState<number>(task.qtyPlan ? Math.max(0, task.qtyPlan - (task.qtyOk || 0)) : 0);
  const [qtyNg, setQtyNg] = useState<number>(0);

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white shadow-sm space-y-3">
      <div className="flex justify-between items-center">
        <div>
          <span className="font-semibold text-sm text-slate-900">{task.processName || "Công đoạn SX"}</span>
          {task.machineName && <span className="text-xs text-slate-500 ml-2">• Máy: {task.machineName}</span>}
        </div>
        <Badge variant="outline">{formatStatusVi(task.status)}</Badge>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-emerald-700 font-semibold">Sản phẩm Đạt (OK)</Label>
          <NumericInput 
            value={qtyOk} 
            onChange={e => setQtyOk(Number(e.target.value))}
            className="h-10 text-center font-bold border-emerald-200 text-emerald-800" 
          />
        </div>
        <div>
          <Label className="text-xs text-rose-700 font-semibold">Phế phẩm (Hỏng / NG)</Label>
          <NumericInput 
            value={qtyNg} 
            onChange={e => setQtyNg(Number(e.target.value))}
            className="h-10 text-center font-bold border-rose-200 text-rose-800" 
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button 
          className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs" 
          onClick={() => onComplete(qtyOk, qtyNg)}
          disabled={isLoading}
        >
          {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
          Xác nhận nhanh
        </Button>
        {onOpenLogForm && (
          <Button
            type="button"
            variant="outline"
            className="h-9 px-3 text-xs font-semibold text-teal-700 border-teal-200 hover:bg-teal-50 flex items-center gap-1"
            onClick={onOpenLogForm}
            title="Mở nhật ký đầy đủ để chọn thợ máy, ca làm việc, thời gian và ghi chú"
          >
            <Zap className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
            <span>Nhập Chi Tiết</span>
          </Button>
        )}
      </div>
    </div>
  );
}

function QuickLogForm({ onComplete, isLoading }: { onComplete: (qtyOk: number, qtyNg: number) => void; isLoading: boolean }) {
  const [qtyOk, setQtyOk] = useState<number>(0);
  const [qtyNg, setQtyNg] = useState<number>(0);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-emerald-700 font-semibold">Sản phẩm Đạt (OK)</Label>
          <NumericInput 
            value={qtyOk} 
            onChange={e => setQtyOk(Number(e.target.value))}
            className="h-10 text-center font-bold border-emerald-200 text-emerald-800" 
          />
        </div>
        <div>
          <Label className="text-xs text-rose-700 font-semibold">Phế phẩm (Hỏng / NG)</Label>
          <NumericInput 
            value={qtyNg} 
            onChange={e => setQtyNg(Number(e.target.value))}
            className="h-10 text-center font-bold border-rose-200 text-rose-800" 
          />
        </div>
      </div>

      <Button 
        className="w-full h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs" 
        onClick={() => onComplete(qtyOk, qtyNg)}
        disabled={isLoading || (qtyOk <= 0 && qtyNg <= 0)}
      >
        {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
        Xác nhận Nhập Sản Lượng
      </Button>
    </div>
  );
}
